import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import Row from 'components/Common/Row';
import Dropdown from 'components/Inputs/Dropdown';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';

import Typography from '../../../kingpin/atoms/Typography';
import Icon from '../../../kingpin/atoms/Icon';
import Badge from '../../../kingpin/atoms/Badge';
import Button from '../../../kingpin/atoms/Button';
import Tooltip from '../../Tooltip';
import useUser from '../../../hooks/useUser';
import getUserDisplayName from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/getUserDisplayName';
import isoDateToAmerican from '../../../isoDateToAmerican';
import appNavItemTypeCheckers from '../../../types/appNavItemTypeCheckers';
import CurrentUserContext from '../../../contexts/CurrentUserContext';
import ImpersonatorContext from '../../../contexts/ImpersonatorContext';
import { V3AppsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import FeatureGate from '../../FeatureGate';
import PermissionGates from '../../PermissionGates';
import useGetBonusRecordTaggingConfig from '../../../screens/Settings/Advanced/BonusRecordTaggingFields/useGetBonusRecordTaggingConfig';
import GqlClientContext from '../../../contexts/GqlClientContext';
import search from '../../../hooks/grids/search';
import { DEFAULT_EXCLUDED_TAG } from './BonusRecordTagging/tagConstants';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import numberToCommaString from '../../../hooks/useValueFormatters/numberToCommaString';
import getRecordTaggingFieldKey from './BonusRecordTagging/getRecordTaggingFieldKey';
import hasActiveRewardOverride from '../utils/hasActiveRewardOverride';

const OverviewDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  &:not(:last-of-type) {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-border-tertiary);
  }
`;

export const RecordTaggingButtonInner = ({
  taggableField,
  fieldType,
  bonusOverview,
}: {
  taggableField: BonusRecordTagging.TaggableFieldOutput;
  fieldType: BonusRecordTagging.FieldType;
  bonusOverview: BonusTypes.BonusOverview;
}) => {
  const { client } = useContext(GqlClientContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { enterRecordTaggingMode, fieldTagCounts } =
    useContext(BonusOverviewContext);
  const getBonusRecordTaggingConfig = useGetBonusRecordTaggingConfig();
  const [isConfigured, setIsConfigured] = useState<boolean | undefined>();
  const [taggedCount, setTaggedCount] = useState<number>(0);

  // Prefer the optimistic count set when a tag is saved this session; fall back
  // to the search2 count loaded on mount (which lags the backend reindex).
  const fieldTagKey = getRecordTaggingFieldKey({
    fieldType,
    fieldName: taggableField.name,
  });
  const optimisticCount = fieldTagCounts[fieldTagKey];
  const displayCount =
    optimisticCount !== undefined ? optimisticCount : taggedCount;

  useEffect(() => {
    let isActive = true;

    (async () => {
      let config: BonusRecordTagging.RecordTaggingConfig | undefined;
      try {
        config = await getBonusRecordTaggingConfig({
          fieldType,
          fieldName: taggableField.name,
          bonusProgramId: bonusOverview.bonusProgram.id,
        });
      } catch {
        if (!isActive) return;
        setIsConfigured(false);
        return;
      }
      if (!isActive) return;
      setIsConfigured(config !== undefined);
      if (!config) return;

      // Count this driver's records carrying the field's configured tag
      // (defaults to EXCLUDED) for the period, via a server-side count.
      const configuredTag = config.tag || DEFAULT_EXCLUDED_TAG;
      try {
        const result = await search({
          dataType: config.dataType,
          filters: [
            {
              dataType: [config.dataType],
              keywords: [
                { field: 'driver', values: [bonusOverview.driver] },
                { field: 'tags', values: [configuredTag] },
              ],
            },
          ],
          dateScope: {
            startDate: bonusOverview.bonusPeriod.startDate,
            endDate: bonusOverview.bonusPeriod.endDate,
            dateField: 'date',
          },
          sortBy: [],
          client,
          includeFields: ['id'],
        });
        if (!isActive) return;
        setTaggedCount(result.count);
      } catch {
        // non-fatal: indicator stays hidden if the tag lookup fails
      }
    })();

    return () => {
      isActive = false;
    };
  }, [
    client,
    taggableField.name,
    fieldType,
    bonusOverview,
    getBonusRecordTaggingConfig,
  ]);

  const exclusionIndicator =
    displayCount > 0 ? (
      <Tooltip
        testId={`exclusion-tag-count-${fieldType}-${taggableField.name}`}
        content={
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {`${displayCount} record${displayCount === 1 ? '' : 's'} tagged for this field`}
          </Typography.Body>
        }
      >
        <Icon
          icon={'info'}
          color={'var(--color-status-warning)'}
          width={12}
          height={12}
        />
      </Tooltip>
    ) : null;

  // Data tagging is not available on archived periods (they are frozen —
  // no recalculation/pay), so the entry point is disabled.
  const isArchivedPeriod = bonusOverview.bonusPeriod.status === 'archived';

  const button = (
    <Button
      onClick={() => {
        trackEvent('Driver Bonus - Exclude Mode Entered', {
          periodId: bonusOverview.bonusPeriod.id,
          criteriaField: taggableField.name,
          fieldName: taggableField.name,
          fieldType,
        });
        enterRecordTaggingMode(taggableField, fieldType);
      }}
      size={'Medium'}
      icon={'chevron-right'}
      label={'View Data'}
      testId={`view-data-${taggableField.name}`}
      type={'Ghost - No Padding'}
      isDisabled={isConfigured === false || isArchivedPeriod}
    />
  );

  if (isArchivedPeriod) {
    return (
      <Row centerAlign style={{ gap: 4 }}>
        {exclusionIndicator}
        <Tooltip
          content={
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              Data tagging is not available for archived bonus periods
            </Typography.Body>
          }
        >
          {button}
        </Tooltip>
      </Row>
    );
  }

  if (isConfigured === false) {
    return (
      <Row centerAlign style={{ gap: 4 }}>
        {exclusionIndicator}
        <Tooltip
          content={
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              No data view configured for this field
            </Typography.Body>
          }
        >
          {button}
        </Tooltip>
      </Row>
    );
  }

  return (
    <Row centerAlign style={{ gap: 4 }}>
      {exclusionIndicator}
      {button}
    </Row>
  );
};

export const RecordTaggingButton = ({
  taggableField,
  fieldType = 'criteria',
}: {
  taggableField: BonusRecordTagging.TaggableFieldOutput | undefined;
  fieldType?: BonusRecordTagging.FieldType;
}) => {
  const { bonusOverview } = useContext(BonusOverviewContext);

  if (!taggableField) return null;
  if (!bonusOverview) return null;
  if (bonusOverview.variant === 'slideout') return null;

  return (
    <FeatureGate featureName={'driverBonusExclusion'}>
      <PermissionGates.Has
        requiredPermission={
          'fleetops.permissions.driver_bonus_include_exclude_records'
        }
      >
        <div data-testid={'exclude-button-container'}>
          <RecordTaggingButtonInner
            taggableField={taggableField}
            fieldType={fieldType}
            bonusOverview={bonusOverview}
          />
        </div>
      </PermissionGates.Has>
    </FeatureGate>
  );
};

export const OverrideTooltip = ({
  msg,
  updatedOn,
  updatedBy,
  valueLabel,
  reasonCodes,
}: {
  msg?: string;
  updatedOn: string;
  updatedBy: string;
  valueLabel?: string;
  // Recorded once for the whole override change, so the same codes show on
  // every field's tooltip within that change.
  reasonCodes?: string[];
}) => {
  const { user } = useUser(updatedBy);
  const updatedDate = useMemo(() => {
    return isoDateToAmerican(updatedOn);
  }, [updatedOn]);

  return (
    <div>
      {valueLabel && (
        <div style={{ marginBottom: 4 }}>
          <Typography.Body
            type={'Body 13 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {valueLabel}
          </Typography.Body>
        </div>
      )}
      {msg && msg !== '' && (
        <div style={{ marginBottom: 4 }}>
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {`Note: '${msg}'`}
          </Typography.Body>
        </div>
      )}

      {!!reasonCodes && reasonCodes.length > 0 && (
        <div style={{ marginBottom: 4 }}>
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {`Reason: ${reasonCodes.join(', ')}`}
          </Typography.Body>
        </div>
      )}

      <Row centerAlign style={{ gap: 8 }}>
        <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
          {user && getUserDisplayName(user)}
        </Typography.Body>
        <Typography.Body type={'Body 13'} color={'var(--color-text-muted)'}>
          {updatedDate}
        </Typography.Body>
      </Row>
    </div>
  );
};

const PassedLabel = () => {
  return (
    <Row centerAlign style={{ gap: 8 }}>
      <Icon icon={'circleCheckmark'} color={'var(--color-status-success)'} />
      <Typography.Body type={'Button Text'}>Passed</Typography.Body>
    </Row>
  );
};

const FailedLabel = () => {
  return (
    <Row centerAlign style={{ gap: 8 }}>
      <Icon icon={'warning'} color={'var(--color-status-danger)'} />
      <Typography.Body type={'Button Text'}>Failed</Typography.Body>
    </Row>
  );
};

const DisabledTooltip = ({
  isBoardOwner,
  isDriverQualified,
}: {
  isBoardOwner: boolean;
  isDriverQualified: boolean;
}) => {
  if (isBoardOwner && isDriverQualified) {
    return null;
  }

  if (!isBoardOwner && !isDriverQualified) {
    return (
      <div>
        <div style={{ marginBottom: 4 }}>
          <Typography.Body
            type={'Body 13 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            You cannot override as:
          </Typography.Body>
        </div>
        <ul style={{ padding: '0px 16px', margin: 0 }}>
          <li>
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              The driver is disqualified
            </Typography.Body>
          </li>
          <li>
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              You are not the board owner
            </Typography.Body>
          </li>
        </ul>
      </div>
    );
  }

  if (!isBoardOwner) {
    return (
      <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
        You cannot override as you are not the board owner
      </Typography.Body>
    );
  }

  return (
    <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
      You cannot override as the driver is disqualified
    </Typography.Body>
  );
};

const OverrideBadge = ({
  persistedOverride,
  persistedValueOverride,
  criteriaDisplayName,
  variant,
  onRemoved,
  reasonCodes,
}: {
  persistedOverride: BonusTypes.Overrides.PersistedCriteria;
  persistedValueOverride?: BonusTypes.Overrides.PersistedValueOverride;
  criteriaDisplayName?: string;
  variant: 'slideout' | 'overview';
  onRemoved?: () => void;
  reasonCodes?: string[];
}) => {
  const label = useMemo(() => {
    const base = persistedOverride.value ? 'Passed' : 'Failed';
    if (
      persistedOverride.value &&
      persistedValueOverride &&
      criteriaDisplayName
    ) {
      return `${base} - ${numberToCommaString({ value: persistedValueOverride.value })} ${criteriaDisplayName}`;
    }
    return base;
  }, [persistedOverride.value, persistedValueOverride, criteriaDisplayName]);

  if (variant === 'slideout') {
    return (
      <div style={{ cursor: 'help' }}>
        <Tooltip
          content={
            persistedOverride.note ? (
              <OverrideTooltip
                {...persistedOverride.note}
                reasonCodes={reasonCodes}
                valueLabel={
                  persistedOverride.value &&
                  persistedValueOverride &&
                  criteriaDisplayName
                    ? `${numberToCommaString({ value: persistedValueOverride.originalValue ?? 0 })} → ${numberToCommaString({ value: persistedValueOverride.value })} ${criteriaDisplayName}`
                    : undefined
                }
              />
            ) : undefined
          }
        >
          <Icon icon={'info'} color={'var(--color-status-warning)'} />
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip
      content={
        persistedOverride.note ? (
          <OverrideTooltip
            {...persistedOverride.note}
            reasonCodes={reasonCodes}
          />
        ) : undefined
      }
    >
      <Badge badgeType={'Orange'} gap={4}>
        <Icon icon={'info'} />
        <Typography.Body type={'Button Text'}>
          {`Manually Set To: ${label}`}
        </Typography.Body>
        {!!onRemoved && (
          <Button
            testId={`remove-${persistedOverride.criterionField}-override`}
            size={'X-Small - Circle'}
            type={'Ghost'}
            onClick={onRemoved}
            icon={'cross'}
            iconColor={'var(--color-icon-secondary)'}
          />
        )}
      </Badge>
    </Tooltip>
  );
};

const ClearCriteriaOverride = ({
  persistedOverride,
  bonusOverview,
  criteriaField,
}: {
  persistedOverride: BonusTypes.Overrides.PersistedCriteria;
  bonusOverview: BonusTypes.BonusOverview;
  criteriaField: BonusTypes.Rewards.CriteriaOutput;
}) => {
  const { setStagedOverrides, stagedOverrides } = bonusOverview;
  const isMarkedForRemoval = useMemo(() => {
    if (!stagedOverrides) {
      return false;
    }

    return stagedOverrides.criteria.some(
      (c) =>
        c.criterionField === persistedOverride.criterionField &&
        c.action === 'Clear',
    );
  }, [persistedOverride.criterionField, stagedOverrides]);

  const onRemoved = useCallback(() => {
    setStagedOverrides((current) => {
      const clearAction: BonusTypes.Overrides.UserInputCriteria = {
        criterionField: persistedOverride.criterionField,
        action: 'Clear',
      };

      if (!current) {
        return {
          criteria: [clearAction],
        };
      }
      return {
        ...current,
        criteria: [...current.criteria, clearAction],
      };
    });
  }, [persistedOverride.criterionField, setStagedOverrides]);

  if (isMarkedForRemoval) {
    return (
      <Row style={{ gap: 8 }} centerAlign>
        <Badge badgeType={'Blue'} gap={4}>
          <Icon icon={'warning'} />
          <Typography.Body type={'Badge'}>Override Removed</Typography.Body>
        </Badge>
        {!criteriaField.passed && <FailedLabel />}
        {criteriaField.passed && <PassedLabel />}
      </Row>
    );
  }

  const persistedValueOverride = bonusOverview.overrides?.valueOverrides.find(
    (v) => v.field === persistedOverride.criterionField,
  );

  // If the reward's payout is directly overridden, its criteria are locked — so don't offer
  // to remove this criteria override either.
  const isRewardOverridden = hasActiveRewardOverride({
    rewardName: criteriaField.reward,
    persistedRewardOverrides: bonusOverview.actualRewardOutput.rewardOverrides,
    stagedRewardOverrides: bonusOverview.stagedOverrides?.rewardOverrides,
  });

  return (
    <OverrideBadge
      persistedOverride={persistedOverride}
      persistedValueOverride={persistedValueOverride}
      criteriaDisplayName={criteriaField.displayName}
      onRemoved={isRewardOverridden ? undefined : onRemoved}
      variant={bonusOverview.variant}
      reasonCodes={bonusOverview.overrides?.reasonCodes}
    />
  );
};

const SetCriteriaOverride = ({
  bonusOverview,
  criteriaField,
}: {
  bonusOverview: BonusTypes.BonusOverview;
  criteriaField: BonusTypes.Rewards.CriteriaOutput;
}) => {
  const [isPassed, setIsPassed] = useState<boolean>(criteriaField.passed);
  const { isLoadingPreview } = useContext(BonusOverviewContext);
  const currentUser = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { availableProductApps } = useContext(V3AppsContext);
  const { setStagedOverrides } = bonusOverview;

  const daxApp = useMemo((): V3.BonusApp | undefined => {
    return availableProductApps
      .filter(appTypeCheckers.isBonusApp)
      .find((app) => app.bonusProgramId === bonusOverview.bonusProgram.id);
  }, [availableProductApps, bonusOverview.bonusProgram.id]);

  const currentUserId = useMemo(() => {
    if (impersonatorId) {
      return impersonatorId;
    }

    return currentUser.id;
  }, [currentUser.id, impersonatorId]);

  // Keep state in sync with the RewardOutput
  // We don't use a memo here as we want to optimistically update this state
  useEffect(() => {
    setIsPassed(criteriaField.passed);
  }, [criteriaField.passed]);

  // Derive from actualRewardOutput (not criteriaField which reflects the live preview).
  // criteriaField.overridden flips each time a staged criteria override is added/removed,
  // causing stagePassedWithCriteria to alternate between the two branches on each selection.
  const isOverridden = useMemo(() => {
    return (
      bonusOverview.actualRewardOutput.criteria.find(
        (c) => c.name === criteriaField.name,
      )?.overridden ?? false
    );
  }, [bonusOverview.actualRewardOutput, criteriaField.name]);

  // Separate from isOverridden: show the indicator whenever there is an active staged
  // override for this field, even before it's confirmed. isOverridden (above) is only
  // used to choose the stagePassed/failed branch and reflects persisted state only.
  const { stagedOverrides } = bonusOverview;
  const hasActiveOverride = useMemo(() => {
    if (isOverridden) return true;
    return (
      stagedOverrides?.criteria.some(
        (c) => c.criterionField === criteriaField.name && c.action === 'Set',
      ) ?? false
    );
  }, [isOverridden, stagedOverrides, criteriaField.name]);

  const isQualified = useMemo(() => {
    return bonusOverview.rewardOutput.eligibleForBonus === 'qualified';
  }, [bonusOverview.rewardOutput.eligibleForBonus]);

  const renderSelectedLabel = useCallback(() => {
    if (isPassed) {
      return <PassedLabel />;
    }
    return <FailedLabel />;
  }, [isPassed]);

  const options = useMemo((): DropdownOption[] => {
    // Metric-tiering (threshold) options were retired with the move to reward-level payout
    // overrides — this control now only sets a criterion Passed/Failed.
    const stageCriteria = (value: boolean) => {
      setIsPassed(value);
      // When a criteria override is already persisted, toggling simply drops any staged
      // override for this field (reverting to the persisted state).
      if (isOverridden) {
        setStagedOverrides((current) => {
          if (!current) {
            return undefined;
          }
          return {
            ...current,
            criteria: current.criteria.filter(
              (c) => c.criterionField !== criteriaField.name,
            ),
          };
        });
        return;
      }

      const newValue: BonusTypes.Overrides.UserInputCriteria = {
        value,
        action: 'Set',
        note: '',
        criterionField: criteriaField.name,
      };
      setStagedOverrides((current) => {
        const filteredCriteria = (current?.criteria ?? []).filter(
          (c) => c.criterionField !== criteriaField.name,
        );
        return {
          ...current,
          criteria: [...filteredCriteria, newValue],
        };
      });
    };

    const passedOption: DropdownOption = {
      key: 'passed',
      label: 'Passed',
      renderLabel: PassedLabel,
      isSelected: isPassed,
      onSelected: () => {
        if (isPassed) {
          return;
        }
        stageCriteria(true);
      },
    };

    const failedOption: DropdownOption = {
      key: 'failed',
      label: 'Failed',
      renderLabel: FailedLabel,
      isSelected: !isPassed,
      onSelected: () => {
        if (!isPassed) {
          return;
        }
        stageCriteria(false);
      },
    };

    return [passedOption, failedOption];
  }, [criteriaField.name, isOverridden, isPassed, setStagedOverrides]);

  const isBoardOwner = useMemo(() => {
    if (!daxApp) {
      return false;
    }

    const signOffBoards: V3.Navigation.SignOffBoard[] =
      daxApp.navigationSettings.sections.reduce(
        (acc: V3.Navigation.SignOffBoard[], section) => {
          const newBoards = section.items.filter(
            appNavItemTypeCheckers.isSignOffBoard,
          );
          return [...acc, ...newBoards];
        },
        [],
      );

    const ownedBoards = signOffBoards.filter((b) =>
      b.signOff.ownerIds.includes(currentUserId),
    );

    return ownedBoards.some((b) => b.categories.includes(criteriaField.reward));
  }, [daxApp, criteriaField.reward, currentUserId]);

  const isAppAdmin = useMemo(() => {
    if (!daxApp) {
      return false;
    }

    if (!impersonatorId && currentUser.isFleetOpsStaff) {
      return true;
    }

    return daxApp.adminUserIds.includes(currentUserId);
  }, [daxApp, currentUser.isFleetOpsStaff, currentUserId, impersonatorId]);

  // A direct payout override on this reward supersedes its criteria — lock the pass/fail control.
  const isRewardOverridden = useMemo(() => {
    return hasActiveRewardOverride({
      rewardName: criteriaField.reward,
      persistedRewardOverrides:
        bonusOverview.actualRewardOutput.rewardOverrides,
      stagedRewardOverrides: bonusOverview.stagedOverrides?.rewardOverrides,
    });
  }, [
    criteriaField.reward,
    bonusOverview.actualRewardOutput.rewardOverrides,
    bonusOverview.stagedOverrides,
  ]);

  const isPermissionDisabled = useMemo((): boolean => {
    return !isQualified || !(isAppAdmin || isBoardOwner);
  }, [isAppAdmin, isBoardOwner, isQualified]);

  if (isPermissionDisabled || isRewardOverridden) {
    return (
      <Tooltip
        content={
          isRewardOverridden ? (
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              This reward&apos;s payout has been overridden, so its criteria no
              longer affect the payout.
            </Typography.Body>
          ) : (
            <DisabledTooltip
              isBoardOwner={isBoardOwner || isAppAdmin}
              isDriverQualified={isQualified}
            />
          )
        }
      >
        <div style={{ opacity: 0.4 }}>
          <Dropdown
            options={options}
            renderSelectedLabel={renderSelectedLabel}
            isDisabled={isLoadingPreview || !isQualified || isRewardOverridden}
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <Row centerAlign style={{ gap: 8 }}>
      {hasActiveOverride && (
        <Tooltip content={`Overridden`}>
          <Icon icon={'info'} color={'var(--color-status-warning)'} />
        </Tooltip>
      )}
      <Dropdown
        options={options}
        renderSelectedLabel={renderSelectedLabel}
        isDisabled={isLoadingPreview}
        testId={`set-criteria-${criteriaField.name}-dropdown`}
      />
    </Row>
  );
};

/**
 * When in override mode;
 * - If an override is persisted, give an option to clear it
 * - Otherwise - give an option to set an override
 *
 * When not in override mode;
 * - If there is a persisted override, show it
 */
const Overrides = ({
  field,
  rewardName,
}: {
  field: string;
  // The reward whose section this criterion is rendered under. A criterion name can be shared
  // across rewards (e.g. `totalMiles` under Utilization, Fuel and Safety), so we must resolve
  // the right instance by name AND reward — otherwise downstream reward-context checks
  // (board ownership, payout-override disabling) key off the wrong reward.
  rewardName?: string;
}) => {
  const { bonusOverview, currentMode } = useContext(BonusOverviewContext);

  if (!bonusOverview) {
    return null;
  }

  const persistedOverride: BonusTypes.Overrides.PersistedCriteria | undefined =
    bonusOverview.overrides
      ? bonusOverview.overrides.criteriaOverrides.find(
          (f) => f.criterionField === field,
        )
      : undefined;

  const criteriaField = bonusOverview.rewardOutput.criteria.find(
    (c) => c.name === field && (rewardName ? c.reward === rewardName : true),
  );

  if (!criteriaField) {
    return null;
  }

  if (currentMode.mode !== 'override') {
    if (persistedOverride) {
      return (
        <span>
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            <RecordTaggingButton taggableField={criteriaField} />
          </div>
          <OverrideBadge
            persistedOverride={persistedOverride}
            persistedValueOverride={bonusOverview.overrides?.valueOverrides.find(
              (v) => v.field === persistedOverride.criterionField,
            )}
            criteriaDisplayName={criteriaField?.displayName}
            variant={bonusOverview.variant}
            reasonCodes={bonusOverview.overrides?.reasonCodes}
          />
        </span>
      );
    }
    return <RecordTaggingButton taggableField={criteriaField} />;
  }

  if (persistedOverride) {
    return (
      <ClearCriteriaOverride
        persistedOverride={persistedOverride}
        bonusOverview={bonusOverview}
        criteriaField={criteriaField}
      />
    );
  }

  return (
    <SetCriteriaOverride
      bonusOverview={bonusOverview}
      criteriaField={criteriaField}
    />
  );
};

const Expectation = ({
  label,
  field,
  isPassed,
  actual,
  target,
  variant,
  renderRight,
  rewardName,
}: {
  label: string;
  field?: string;
  isPassed: boolean;
  actual: string;
  target: string;
  variant: 'slideout' | 'overview' | 'tooltip';
  // Right-hand control for rows that aren't criteria (qualifiers/disqualifiers),
  // which don't route through the criteria `Overrides`. Only used by the
  // 'overview' variant.
  renderRight?: React.ReactNode;
  // The reward this criterion belongs to (threaded so shared criterion names resolve correctly).
  rewardName?: string;
}) => {
  if (variant === 'slideout' || variant === 'tooltip') {
    return (
      <>
        <Row spaceBetween centerAlign style={{ gap: 16 }}>
          <Row centerAlign style={{ gap: 4 }}>
            <Typography.Body type={'Label'}>{label}</Typography.Body>
            {field && <Overrides field={field} rewardName={rewardName} />}
          </Row>
          <Row centerAlign style={{ gap: 8 }}>
            <Typography.Body
              type={'Label'}
            >{`${actual} / ${target}`}</Typography.Body>
            <Icon
              icon={isPassed ? 'checkmark' : 'cross'}
              color={
                isPassed
                  ? 'var(--color-status-success)'
                  : 'var(--color-status-danger)'
              }
            />
          </Row>
        </Row>
      </>
    );
  }

  return (
    <OverviewDiv>
      <Row centerAlign style={{ gap: 8 }}>
        <Icon
          icon={isPassed ? 'checkmark' : 'cross'}
          color={
            isPassed
              ? 'var(--color-status-success)'
              : 'var(--color-status-danger)'
          }
          width={18}
          height={18}
        />
        <div>
          <div>
            <Typography.Header type={'H3'}>{label}</Typography.Header>
          </div>
          <Typography.Body
            type={'Label'}
            color={'var(--color-text-secondary)'}
          >{`${actual} / ${target}`}</Typography.Body>
        </div>
      </Row>
      {field ? (
        <Overrides field={field} rewardName={rewardName} />
      ) : (
        renderRight
      )}
    </OverviewDiv>
  );
};

export default Expectation;
